import { useCallback, useEffect, useMemo, useRef } from "react";
import { TextInput } from "react-native";
import { usePaginatedQuery } from "convex/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { getDateChangeMap } from "@/elements/components/utils/helpers";
import { Id } from "@teachloop/backend/_generated/dataModel";
import Toast from "react-native-toast-message";
import * as DocumentPicker from "expo-document-picker";

interface UseChatOptions {
  chatId: Id<"chats"> | undefined;
  userType: "tutor" | "parent";
}

export function useChat({ chatId, userType }: UseChatOptions) {
  const { data: currentUser } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {})
  );

  const queryApi =
    userType === "tutor"
      ? api.tutor.inbox.queries.getMessages
      : api.parent.inbox.queries.getMessages;

  const mutationApi =
    userType === "tutor"
      ? api.tutor.inbox.mutations.sendMessage
      : api.parent.inbox.mutations.sendMessage;

  const mutationApiGenerateUploadUrl =
    userType === "tutor"
      ? api.tutor.inbox.mutations.generateUploadUrl
      : api.parent.inbox.mutations.generateUploadUrl;

  const mutationApiMarkAsRead =
    userType === "tutor"
      ? api.tutor.inbox.mutations.markMessagesAsRead
      : api.parent.inbox.mutations.markMessagesAsRead;

  const { results, loadMore, isLoading } = usePaginatedQuery(
    queryApi,
    chatId ? { chatId } : "skip",
    { initialNumItems: 20 }
  );

  const latestMessage = useMemo(() => results[0], [results]);

  const dateChangeMap = useMemo(() => getDateChangeMap(results), [results]);

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: useConvexMutation(mutationApi),
  });

  const { mutateAsync: markMessagesAsRead } = useMutation({
    mutationFn: useConvexMutation(mutationApiMarkAsRead),
  });

  const { mutateAsync: generateUploadUrl, isPending: isGeneratingUploadUrl } =
    useMutation({
      mutationFn: useConvexMutation(mutationApiGenerateUploadUrl),
    });

  const { mutateAsync: uploadFile, isPending: isUploadingFile } = useMutation({
    mutationFn: async ({
      uploadUrl,
      file,
      fileName,
      mimeType,
    }: {
      uploadUrl: string;
      file: Blob;
      fileName: string;
      mimeType: string;
    }) => {
      if (!chatId) return;
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: file,
        headers: {
          "Content-Type": mimeType,
        },
      });
      if (response.ok) {
        const { storageId } = await response.json();
        if (storageId) {
          await sendMessage({
            chatId,
            fileId: storageId,
            fileName,
            fileType: mimeType,
          });
        }
      }
    },
  });
  const isUploading = isGeneratingUploadUrl || isUploadingFile;

  const pickFile = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      multiple: false,
      type: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/png",
        "image/heic",
        "image/heif",
      ],
    });
    const [asset] = result.assets ?? [];
    if (asset) {
      if (asset.size && asset.size > 5 * 1024 * 1024) {
        Toast.show({ type: "error", text1: "File size cannot exceed 5MB" });
        return;
      }
      if (!asset.file) {
        Toast.show({ type: "error", text1: "File not found" });
        return;
      }

      const uploadUrl = await generateUploadUrl({});
      await uploadFile({
        uploadUrl,
        fileName: asset.name,
        file: asset.file,
        mimeType: asset.file.type,
      });
    }
  };

  const inputRef = useRef<TextInput | null>(null);

  const handleSendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !chatId) return;

      try {
        await sendMessage({
          chatId,
          content: text,
          fileId: undefined,
          fileName: undefined,
          fileType: undefined,
        });
        inputRef.current?.clear();
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    },
    [sendMessage, chatId]
  );

  const renderMessage = useCallback(
    ({ item }: { item: any }) => {
      const isMe = item.sentBy._id === currentUser?._id;
      return {
        ...item,
        isMe,
        showDate: !!dateChangeMap[item._id],
      };
    },
    [currentUser?._id, dateChangeMap]
  );

  const handleLoadMore = useCallback(() => {
    loadMore(20);
  }, [loadMore]);

  useEffect(() => {
    if (!chatId) return;
    markMessagesAsRead({
      chatId,
      readUntilTimestamp: Date.now(),
    });
  }, [markMessagesAsRead, chatId, latestMessage?._id]);

  return {
    currentUser,
    results,
    isLoading,
    isPending,
    inputRef,
    handleSendMessage,
    renderMessage,
    handleLoadMore,
    pickFile,
    isUploading,
  };
}
